import { useState, useEffect } from 'react'
import { useNavigate, useLocation, Outlet, Navigate } from 'react-router-dom'
import { supabase } from './supabaseClient'
import { subscribeToWhatsAppStatus, getWhatsAppStatus } from './WhatsAppConexao'
import PerfilUsuario from './PerfilUsuario'
import TrialExpiredModal from './TrialExpiredModal'
import { useTrialStatus } from './useTrialStatus'
import { useUser } from './contexts/UserContext'
import { useWorkspace } from './contexts/WorkspaceContext'
import { useTheme } from './contexts/ThemeContext'
import { usePaymentNotifications } from './hooks/usePaymentNotifications'
import { Icon } from '@iconify/react'
import useWindowSize from './hooks/useWindowSize'
import TopToolbar from './components/TopToolbar'
import SidebarWorkspaceSelector from './components/SidebarWorkspaceSelector'
import './components/Sidebar.css'

export default function Dashboard() {
  const navigate = useNavigate()
  const location = useLocation()
  const { isMobile } = useWindowSize()
  const { theme } = useTheme()
  const { workspaces, currentWorkspace, switchWorkspace, saldo, loading: workspaceLoading } = useWorkspace()
  
  const [mostrarPerfil, setMostrarPerfil] = useState(false)
  const [whatsappStatus, setWhatsappStatus] = useState(getWhatsAppStatus())
  const [mostrarModalTrial, setMostrarModalTrial] = useState(false)
  
  // Menu começa aberto no desktop, fechado no mobile
  const [menuAberto, setMenuAberto] = useState(!isMobile)
  const [configSubmenuAberto, setConfigSubmenuAberto] = useState(false)

  // Hook para verificar status do trial
  const { isExpired, diasRestantes, planoPago, loading } = useTrialStatus()
  const { userData, userId, isSuperAdmin } = useUser()

  // Notificacoes em tempo real de pagamentos
  usePaymentNotifications(userId)

  // Determinar tela ativa pela rota atual
  const telaAtiva = location.pathname.replace('/app/', '') || 'home'

  useEffect(() => {
    // Inscrever-se para atualizações do status do WhatsApp
    const unsubscribe = subscribeToWhatsAppStatus((newStatus) => {
      setWhatsappStatus(newStatus)
    })
    return unsubscribe
  }, [])

  // Mostrar modal se trial expirou
  useEffect(() => {
    if (!loading && isExpired && !planoPago) {
      setMostrarModalTrial(true)
    }
  }, [isExpired, planoPago, loading])

  const handleLogout = async () => {
    await supabase.auth.signOut()
    window.location.reload()
  }

  const handleSwitchWorkspace = (workspace) => {
    switchWorkspace(workspace)
  }

  // Redirecionar para onboarding se não completou
  if (!loading && userData && userData.onboarding_completed === false) {
    return <Navigate to="/app/onboarding" replace />
  }

  // Se trial expirou, bloquear acesso
  if (isExpired && !planoPago && !loading) {
    return (
      <>
        <div style={{ display: 'flex', backgroundColor: 'var(--bg-secondary)', height: '100vh', width: '100%', overflow: 'hidden', filter: 'blur(5px)', pointerEvents: 'none' }}>
          {/* Dashboard borrado no fundo */}
        </div>
        <TrialExpiredModal
          diasRestantes={0}
          onClose={() => {}}
          onUpgrade={() => navigate('/app/upgrade')}
        />
      </>
    )
  }

  // Verifica status do Asaas
  const asaasPending = userData?.asaas_account_status === 'PENDING' || (userData?.modo_integracao === 'asaas' && !userData?.asaas_account_id)

  return (
    <div style={{ display: 'flex', backgroundColor: 'var(--bg-secondary)', height: '100vh', width: '100%', overflow: 'hidden' }}>
      {/* Modal de Trial */}
      {mostrarModalTrial && diasRestantes > 0 && diasRestantes <= 2 && (
        <TrialExpiredModal
          diasRestantes={diasRestantes}
          onClose={() => setMostrarModalTrial(false)}
          onUpgrade={() => navigate('/app/upgrade')}
        />
      )}

      {/* Overlay para fechar menu em mobile */}
      {isMobile && menuAberto && (
        <div
          onClick={() => setMenuAberto(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 99
          }}
        />
      )}

      {/* Botão hamburguer - mobile only */}
      {isMobile && (
        <button
          onClick={() => setMenuAberto(!menuAberto)}
          style={{
            position: 'fixed',
            top: '16px',
            left: '16px',
            zIndex: 101,
            width: '44px',
            height: '44px',
            backgroundColor: 'var(--bg-card)',
            border: '1px solid var(--border-primary)',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}
        >
          {menuAberto ? (
            <Icon icon="mdi:close" width="24" height="24" color="#333" />
          ) : (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3 6H21M3 12H21M3 18H21" stroke="#333" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          )}
        </button>
      )}

      {/* Menu lateral */}
      <div 
        className="sidebar-container"
        style={{
          width: isMobile ? '280px' : (menuAberto ? '280px' : '70px'),
          position: isMobile ? 'fixed' : 'relative',
          left: isMobile ? (menuAberto ? '0' : '-280px') : '0',
          zIndex: 100,
          padding: (!menuAberto && !isMobile) ? '0 8px' : '0 16px',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
        }}
      >
        {/* Botão para expandir/colapsar menu - desktop only */}
        {!isMobile && (
          <button
            onClick={() => setMenuAberto(!menuAberto)}
            style={{
              position: 'absolute',
              top: '32px',
              right: '-12px',
              width: '24px',
              height: '24px',
              backgroundColor: 'var(--bg-card)',
              border: '1px solid var(--border-primary)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              zIndex: 101
            }}
            title={menuAberto ? 'Recolher menu' : 'Expandir menu'}
          >
            <Icon 
              icon={menuAberto ? "mdi:chevron-left" : "mdi:chevron-right"} 
              width="16" 
              height="16" 
              color="#666" 
            />
          </button>
        )}

        {/* HEADER: Logo */}
        <div className="sidebar-header" style={{
          padding: (!menuAberto && !isMobile) ? '24px 0 16px' : '24px 8px 16px',
          justifyContent: (!menuAberto && !isMobile) ? 'center' : 'flex-start'
        }}>
          {(menuAberto || isMobile) ? (
            <div style={{ display: 'flex', alignItems: 'center', padding: '0 8px' }}>
              <img 
                src={theme === 'dark' ? '/logo escuro.svg' : '/logoclaro.svg'} 
                alt="Logo" 
                style={{ height: '40px', width: 'auto' }} 
              />
            </div>
          ) : (
            <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
               <img 
                src="/icone.svg" 
                alt="Logo" 
                style={{ height: '32px', width: '32px' }} 
              />
            </div>
          )}
        </div>

        {/* Workspace Selector - ABAIXO DO LOGO */}
        <SidebarWorkspaceSelector
          workspaces={workspaces}
          currentWorkspace={currentWorkspace}
          onSwitchWorkspace={handleSwitchWorkspace}
          saldo={saldo}
          isMobile={isMobile}
          menuAberto={menuAberto}
        />

        {/* MENU PRINCIPAL */}
        <div className="sidebar-menu">
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                {/* Header Principal */}
                {(menuAberto || isMobile) && (
                  <div style={{ padding: '0 16px', marginBottom: '4px', fontSize: '11px', fontWeight: 600, color: '#98a2b3', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Principal
                  </div>
                )}
                
                {/* Home */}
                <MenuItem
                  icon="ph:house"
                  label="Início"
                  active={telaAtiva === 'home'}
                  onClick={() => { navigate('/app/home'); if (isMobile) setMenuAberto(false) }}
                  expanded={menuAberto || isMobile}
                />

                {/* Clientes */}
                <MenuItem
                  icon="ph:users"
                  label="Clientes"
                  active={telaAtiva === 'clientes'}
                  onClick={() => { navigate('/app/clientes'); if (isMobile) setMenuAberto(false) }}
                  expanded={menuAberto || isMobile}
                />

                {/* Financeiro */}
                <MenuItem
                  icon="ph:currency-dollar"
                  label="Financeiro"
                  active={telaAtiva === 'financeiro'}
                  onClick={() => { navigate('/app/financeiro'); if (isMobile) setMenuAberto(false) }}
                  expanded={menuAberto || isMobile}
                />

                {/* Contrato */}
                <MenuItem
                  icon="ph:file-text"
                  label="Contratos"
                  active={telaAtiva === 'contrato'}
                  onClick={() => { navigate('/app/contrato'); if (isMobile) setMenuAberto(false) }}
                  expanded={menuAberto || isMobile}
                />

                {/* Consulta Serasa */}
                <MenuItem
                  icon="ph:magnifying-glass"
                  label="Consulta Serasa"
                  active={telaAtiva === 'serasa'}
                  onClick={() => { navigate('/app/serasa'); if (isMobile) setMenuAberto(false) }}
                  expanded={menuAberto || isMobile}
                />

                {/* Saque */}
                <MenuItem
                  icon="ph:bank"
                  label="Saque"
                  active={telaAtiva === 'saque'}
                  onClick={() => { navigate('/app/saque'); if (isMobile) setMenuAberto(false) }}
                  expanded={menuAberto || isMobile}
                />

                 {/* Nota Fiscal */}
                <MenuItem
                  icon="ph:receipt"
                  label="Notas Fiscais"
                  active={telaAtiva === 'notas-fiscais'}
                  onClick={() => { navigate('/app/notas-fiscais'); if (isMobile) setMenuAberto(false) }}
                  expanded={menuAberto || isMobile}
                />

                {/* Indique e Ganhe */}
                <MenuItem
                  icon="ph:gift"
                  label="Indique e Ganhe"
                  active={telaAtiva === 'parceiros'}
                  onClick={() => { navigate('/app/parceiros'); if (isMobile) setMenuAberto(false) }}
                  expanded={menuAberto || isMobile}
                />
            </div>

            <div style={{ marginTop: 'auto', paddingTop: '16px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                {/* Plan Card */}
                {(menuAberto || isMobile) && (
                  <div style={{ padding: '0 16px', marginBottom: '16px' }}>
                    <div style={{
                      backgroundColor: 'var(--bg-card)',
                      borderRadius: '12px',
                      padding: '16px',
                      border: '1px solid var(--border-primary)',
                      boxShadow: 'var(--shadow-sm)'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                        <span style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-primary)' }}>Plano</span>
                        <span style={{ 
                          backgroundColor: '#eff8ff', 
                          color: '#175cd3', 
                          fontSize: '12px', 
                          fontWeight: '500', 
                          padding: '2px 8px', 
                          borderRadius: '6px',
                          border: '1px solid #b2ddff'
                        }}>
                          {planoPago ? 'PRO' : 'TRIAL'}
                        </span>
                      </div>
                      
                      <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '12px' }}>
                        {planoPago ? 'Renovação em 12/11' : `Expira em ${diasRestantes} dias`}
                      </div>

                      <button 
                        onClick={() => navigate('/app/configuracao?aba=planos')}
                        style={{
                          width: '100%',
                          backgroundColor: 'var(--bg-secondary)',
                          border: '1px solid var(--border-secondary)',
                          borderRadius: '8px',
                          padding: '8px',
                          fontSize: '13px',
                          fontWeight: '600',
                          color: 'var(--text-secondary)',
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                        onMouseOver={(e) => {
                          e.currentTarget.style.backgroundColor = 'var(--bg-tertiary)';
                          e.currentTarget.style.borderColor = 'var(--border-primary)';
                        }}
                        onMouseOut={(e) => {
                          e.currentTarget.style.backgroundColor = 'var(--bg-secondary)';
                          e.currentTarget.style.borderColor = 'var(--border-secondary)';
                        }}
                      >
                        Atualizar plano
                      </button>
                    </div>
                  </div>
                )}

                {/* Header Ajustes */}
                {(menuAberto || isMobile) && (
                  <div style={{ padding: '0 16px', marginBottom: '4px', fontSize: '11px', fontWeight: 600, color: '#98a2b3', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Ajustes
                  </div>
                )}

                {/* Configurações */}
                <MenuItem
                  icon="ph:gear"
                  label="Configurações"
                  active={telaAtiva === 'configuracao'}
                  onClick={() => { navigate('/app/configuracao'); if (isMobile) setMenuAberto(false) }}
                  expanded={menuAberto || isMobile}
                />

                {/* Super Admin - só aparece para superadmins */}
                {isSuperAdmin && (
                  <>
                    {(menuAberto || isMobile) && (
                      <div style={{ padding: '0 16px', margin: '16px 0 4px', fontSize: '11px', fontWeight: 600, color: '#7c3aed', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        Admin
                      </div>
                    )}
                    <MenuItem
                      icon="ph:shield-star"
                      label="Super Admin"
                      active={telaAtiva === 'alean'}
                      onClick={() => { navigate('/app/alean'); if (isMobile) setMenuAberto(false) }}
                      expanded={menuAberto || isMobile}
                    />
                  </>
                )}
            </div>
        </div>
      </div>

      {/* Conteúdo principal */}
      <div style={{
        flex: 1,
        height: '100vh',
        overflow: 'auto',
        display: 'flex',
        flexDirection: 'column',
        paddingTop: isMobile ? '70px' : '0'
      }}>
        {/* Top Toolbar */}
        {!isMobile && (
          <TopToolbar 
            user={userData} 
            saldo={saldo}
            onLogout={handleLogout}
            isMobile={isMobile}
          />
        )}

        {/* Alerta de Configuração Asaas Pendente */}
        {asaasPending && !isMobile && (
          <div style={{
            margin: '20px 24px 0',
            padding: '16px',
            backgroundColor: '#fffbeb',
            border: '1px solid #fcd34d',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{
                width: '36px', height: '36px', borderRadius: '50%', backgroundColor: '#fef3c7',
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>
                <Icon icon="mdi:alert" color="#d97706" width="20" />
              </div>
              <div>
                <h4 style={{ margin: 0, color: '#92400e', fontSize: '14px' }}>Finalize seu cadastro financeiro</h4>
                <p style={{ margin: '4px 0 0', color: '#b45309', fontSize: '13px' }}>
                  Para emitir boletos e receber pagamentos, você precisa concluir seu cadastro no Asaas.
                </p>
              </div>
            </div>
            <button 
              onClick={() => window.open('https://www.asaas.com/auth/login', '_blank')}
              style={{
                backgroundColor: '#d97706',
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '6px',
                fontSize: '13px',
                fontWeight: 500,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              Finalizar no Asaas <Icon icon="mdi:open-in-new" width="14" />
            </button>
          </div>
        )}

        <Outlet />
      </div>

      {/* Modal de Perfil */}
      {mostrarPerfil && (
        <PerfilUsuario onClose={() => setMostrarPerfil(false)} />
      )}
    </div>
  )
}

// Componente de item do menu
function MenuItem({ icon, label, active, onClick, expanded }) {
  return (
    <div
      className={`menu-item ${active ? 'active' : ''}`}
      onClick={onClick}
      style={{
        justifyContent: expanded ? 'flex-start' : 'center',
        padding: expanded ? '10px 16px' : '10px',
      }}
      title={!expanded ? label : ''}
    >
      <Icon icon={icon} width="20" height="20" className="icon" />
      {expanded && <span>{label}</span>}
    </div>
  )
}
